import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { GenericAdminFilterLanguage, Router as RouterLang, TelModuleStatusListLanguage } from '../../../../../i18n'
import { UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getTelModuleStatusList } from '../../../../React/Common/API/ItemsGetters'
import {
    TelModuleStatusListItem,
    TelModuleStatusListResponse,
    TelModuleStatusListRow
} from '../../../../NonReact/Types/TelModuleStatusListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import type { TelModuleStatusQueryParams } from '../../../../NonReact/constants/ApiConstants'
import { parseAdminListUrlSearchParams, type AdminListUrlBootstrap } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialAdminScopeFilters } from '../../../Utils/adminListFilterReadiness'

type TelModuleStatusScope = {
    readonly cmeId: string
    readonly recursive: boolean
    readonly telSysId: string
}

const isConcreteTelSysFilterValue = (value: unknown): boolean =>
    isVarNotEmpty(value) &&
    value !== ALL_VALUE &&
    value !== ANY_VALUE_STRING_NUMERIC &&
    value !== EMPTY_STRING

/**
 * Routed telephone module status list (read-only view of {@code CTelModuleStatus} records).
 *
 * @returns Telephone module status page content.
 */
export const TelModuleStatusList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(TelModuleStatusListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [rows, setRows]: ReactState<TelModuleStatusListRow[]> = useState<TelModuleStatusListRow[]>([])

    const {
        loading,
        setLoading,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState(25)

    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])

    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const [hasCompletedInitialListFetch, setHasCompletedInitialListFetch]: ReactState<boolean> = useState<boolean>(false)

    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad

    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING

    const listTelSysIdParam: string = isConcreteTelSysFilterValue(filters?.telephonyServer?.value)
        ? String(filters?.telephonyServer?.value)
        : String(ALL_VALUE)

    const listScope: OnlyNullable<TelModuleStatusScope> =
        useMemo((): OnlyNullable<TelModuleStatusScope> => {
            if (IsVarEmpty(listCmeId)) {
                return null
            }

            return {
                cmeId: listCmeId,
                recursive: listIncludeRecursive,
                telSysId: listTelSysIdParam
            }
        }, [listCmeId, listIncludeRecursive, listTelSysIdParam])

    useAdminListLegacyServletSwitch(ServletCmds.TELMODULE_STATUS, filters, userInfo?.Scope?.CMEnvironmentID)

    const getList = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listScope)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasCompletedInitialListFetch(true)

            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch.trim() === EMPTY_STRING ? undefined : listSearch.trim()
        const query: TelModuleStatusQueryParams = {
            ...listScope,
            offset: pageStartIndex,
            limit: rowsPerPage,
            ...(isVarNotEmpty(searchQuery) ? { searchQuery } : {})
        }

        getTelModuleStatusList(query)
            .then((response: Nullable<TelModuleStatusListResponse>): void => {
                if (!isMounted.current) return

                const items: readonly TelModuleStatusListItem[] = response?.Data?.Items ?? []
                const mapped: TelModuleStatusListRow[] = items.map(
                    (item: TelModuleStatusListItem, index: number): TelModuleStatusListRow => ({
                        key: `${item.TelSysID ?? EMPTY_STRING}-${item.ClassName ?? EMPTY_STRING}-${String(pageStartIndex)}-${String(index)}`,
                        Description: item.ClassName ?? EMPTY_STRING,
                        TelSysID: item.TelSysID ?? EMPTY_STRING,
                        TelSysDescription: item.TelSysDescription ?? EMPTY_STRING,
                        ClassName: item.ClassName ?? EMPTY_STRING,
                        Type: item.Type ?? EMPTY_STRING,
                        Version: item.Version ?? EMPTY_STRING,
                        Status: item.Status ?? EMPTY_STRING,
                        LastErrorText: item.LastErrorText ?? EMPTY_STRING,
                        LastErrorDateTime: item.LastErrorDateTime ?? EMPTY_STRING
                    })
                )

                setRows(mapped)
                setTotalCount(response?.Data?.Total ?? 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)

                if (isMounted.current) {
                    setLoading((currentLoading: number): number => currentLoading - 1)
                    setHasCompletedInitialListFetch(true)
                }
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listScope,
        listSearch,
        pagination,
        setLoading,
        setTotalCount
    ])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect((): void => {
        getList()
    }, [getList])

    const refreshList = useCallback((): void => {
        getList()
    }, [getList])

    const isTableLoading: boolean =
        loading > 0 ||
        !hasReceivedInitialFilters ||
        isWaitingForInitialUrlBootstrapLoad ||
        (hasReceivedInitialFilters && isStringNotEmpty(listCmeId ?? EMPTY_STRING) && !hasCompletedInitialListFetch)

    const columns: DataTableColumn<TelModuleStatusListRow, keyof TelModuleStatusListRow>[] = useMemo(
        (): DataTableColumn<TelModuleStatusListRow, keyof TelModuleStatusListRow>[] => [
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_TelSys),
                field: 'TelSysDescription',
                key: 'tel-module-status-telsys',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_ClassName),
                field: 'ClassName',
                key: 'tel-module-status-class',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_Type),
                field: 'Type',
                key: 'tel-module-status-type',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_Version),
                field: 'Version',
                key: 'tel-module-status-version',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_Status),
                field: 'Status',
                key: 'tel-module-status-status',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_LastError),
                field: 'LastErrorText',
                key: 'tel-module-status-last-error',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Column_LastErrorDateTime),
                field: 'LastErrorDateTime',
                key: 'tel-module-status-last-error-dt',
                sortable: false,
                align: 'left'
            }
        ],
        [language]
    )

    return (
        <div className='tel-module-status-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_telModuleStatus} />
            <div className='tel-module-status-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialAdminScopeFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={refreshList}
                />
                <DataTable<TelModuleStatusListRow, keyof TelModuleStatusListRow>
                    permissionName={AdminBackendPermissionNames.TEL_SYS_STATUS}
                    localeText={{
                        noRows: language.get(TelModuleStatusListLanguage.TelModuleStatusList_Empty)
                    }}
                    columns={columns}
                    data={rows}
                    defaultRowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                    defaultRowsPerPage={25}
                    totalCount={totalCount}
                    isLoading={isTableLoading}
                    paginationServer
                    toolbarDisabled
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
            </div>
        </div>
    )
}
